import {Trans} from '@lingui/macro';
import React, {ReactNode} from 'react';

import {countTrueValues} from '../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {UserThemeConsumer} from '../../../libs/shared/ui/src/theme/userThemeConsumer';
import {TabButtonElement} from '../../analytics/components/tabs/tabButtonElement';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {
  computeIsGlobalNamespace,
  isTeammateNamespace,
  isTeamNamespace,
} from '../../helpers/models/namespaceHelpers';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {CompanyFeaturesEnum} from '../../models/api/companyModel';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {SettingsResourcePagesEnum, SettingsRulesUrl} from '../../models/url/settingsUrlModel';
import {
  getCompanyHasGlobalRules,
  getFindHasCompanyFeature,
  getIsInTrialOrManagedTrial,
} from '../../store/data/company/companySelectors';
import {getUserNamespace} from '../../store/data/teammates/teammatesSelectors';
import {getFlatTeams} from '../../store/data/teams/teamsSelectors';
import {getHasUpsellCards} from '../../store/featureFlags/featureFlagsSelectors';
import {SettingsHeader} from '../settingsHeader';

/*
 * Consts.
 */

const smallYellowDiamondIcon = (
  <UserThemeConsumer>
    {(theme) => <Icon name="diamondSmall" color={theme.palette.yellow.shade40} />}
  </UserThemeConsumer>
);

/*
 * Props.
 */

interface RuleHeaderProps {
  rulesUrl: SettingsRulesUrl;
  right?: ReactNode;
}

/*
 * Component.
 */

export const RuleHeader: React.FC<RuleHeaderProps> = (props) => {
  const {rulesUrl, children, right} = props;

  const hasCompanyFeature = useAppSelector(getFindHasCompanyFeature);
  const rulesNamespace = rulesUrl.namespace;

  const hasUpsellCard = useAppSelector(getHasUpsellCards);
  const isTrialOrManagedTrial = useAppSelector(getIsInTrialOrManagedTrial);
  const userNamespace = useAppSelector(getUserNamespace);

  const canUseCompanyRules = hasCompanyFeature(CompanyFeaturesEnum.GLOBAL_RULES);
  const upsellCompanyRules = !canUseCompanyRules && hasUpsellCard && !isTrialOrManagedTrial;
  const canUseLoadBalancing = hasCompanyFeature(CompanyFeaturesEnum.ADVANCED_RULES);
  const upsellLoadBalancing = !canUseLoadBalancing && hasUpsellCard && !isTrialOrManagedTrial;

  const {navigateToLink} = useLinkNavigation();
  const redirectToTab = (resourcePage: SettingsResourcePagesEnum, namespace: string) =>
    navigateToLink(buildSettingsPath({...rulesUrl, namespace, resourcePage}));

  const {
    showCompanyTab,
    showIndividualTab,
    showLoadBalancingTab,
    showRuleLibraryTab,
    showTeamTab,
    teamRuleNamespace,
    hasTabs,
  } = useComputeRuleTabs(rulesUrl);

  const renderTabs = () => {
    if (!hasTabs) {
      return null;
    }

    return (
      <>
        {showTeamTab && (
          <TabButtonElement
            interactionId="settings-rules-team-rules"
            name={<Trans id="settings-rules-team-rules">Team rules</Trans>}
            isSelected={
              rulesUrl.resourcePage === SettingsResourcePagesEnum.OVERVIEW && isTeamNamespace(rulesNamespace)
            }
            onTabClick={() => redirectToTab(SettingsResourcePagesEnum.OVERVIEW, teamRuleNamespace)}
          />
        )}
        {showCompanyTab && (
          <TabButtonElement
            interactionId="settings-rules-company-rules"
            name={<Trans id="settings-rules-company-rules">Company rules</Trans>}
            isSelected={
              rulesUrl.resourcePage === SettingsResourcePagesEnum.OVERVIEW &&
              computeIsGlobalNamespace(rulesNamespace)
            }
            rightIcon={upsellCompanyRules ? smallYellowDiamondIcon : undefined}
            onTabClick={() => redirectToTab(SettingsResourcePagesEnum.OVERVIEW, 'global')}
          />
        )}
        {showRuleLibraryTab && (
          <TabButtonElement
            interactionId="settings-rules-rule-library"
            name={<Trans id="settings-rules-rule-library">Rule library</Trans>}
            isSelected={rulesUrl.resourcePage === SettingsResourcePagesEnum.LIBRARY}
            onTabClick={() => redirectToTab(SettingsResourcePagesEnum.LIBRARY, teamRuleNamespace)}
          />
        )}
        {showLoadBalancingTab && (
          <TabButtonElement
            interactionId="settings-rules-load-balancing"
            name={<Trans id="settings-rules-load-balancing">Load balancing</Trans>}
            isSelected={rulesUrl.resourcePage === SettingsResourcePagesEnum.LOAD_BALANCING}
            rightIcon={upsellLoadBalancing ? smallYellowDiamondIcon : undefined}
            onTabClick={() => redirectToTab(SettingsResourcePagesEnum.LOAD_BALANCING, teamRuleNamespace)}
          />
        )}
        {showIndividualTab && (
          <TabButtonElement
            name={<Trans id="settings-rules-individual-rules">Individual rules</Trans>}
            isSelected={isTeammateNamespace(rulesNamespace)}
            onTabClick={() => redirectToTab(SettingsResourcePagesEnum.OVERVIEW, userNamespace)}
          />
        )}
      </>
    );
  };

  return (
    <SettingsHeader tabs={renderTabs()} right={right}>
      {children}
    </SettingsHeader>
  );
};

/*
 * Helpers.
 */

export function useComputeRuleTabs(rulesUrl: SettingsRulesUrl) {
  const teams = useAppSelector(getFlatTeams);
  const hasCompanyFeature = useAppSelector(getFindHasCompanyFeature);
  const rulesNamespace = rulesUrl.namespace;

  const hasUpsellCard = useAppSelector(getHasUpsellCards);

  const canUseCompanyRules = useAppSelector(getCompanyHasGlobalRules);
  const canUseLoadBalancing = hasCompanyFeature(CompanyFeaturesEnum.ADVANCED_RULES);

  const showSingleTeamTabs =
    teams.length === 1 && (computeIsGlobalNamespace(rulesNamespace) || isTeamNamespace(rulesNamespace));

  const showTeamTab = isTeamNamespace(rulesNamespace) || showSingleTeamTabs;
  const showCompanyTab =
    (canUseCompanyRules || hasUpsellCard) && (computeIsGlobalNamespace(rulesNamespace) || showSingleTeamTabs);
  const showRuleLibraryTab = isTeamNamespace(rulesNamespace) || showSingleTeamTabs;
  const showLoadBalancingTab =
    (canUseLoadBalancing || hasUpsellCard) && (isTeamNamespace(rulesNamespace) || showSingleTeamTabs);
  const showIndividualTab = isTeammateNamespace(rulesNamespace);

  const teamRuleNamespace = showSingleTeamTabs ? teams[0].namespace : rulesNamespace;

  const hasTabs =
    countTrueValues(
      showTeamTab,
      showCompanyTab,
      showRuleLibraryTab,
      showLoadBalancingTab,
      showIndividualTab,
    ) > 1;

  return {
    showTeamTab,
    showCompanyTab,
    showRuleLibraryTab,
    showLoadBalancingTab,
    showIndividualTab,
    teamRuleNamespace,
    hasTabs,
  };
}
